import axiosInstance from '../../../config/axios';

async function fetchWithRetry<T>(
  fetchFn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error: any) {
      const isServerError = error?.response?.status >= 500;
      const shouldRetry = isServerError && attempt < maxRetries;

      if (!shouldRetry) {
        throw error;
      }

      const delay = initialDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, Math.min(delay, 8000)));
    }
  }

  throw new Error('Failed after all retries');
}

export interface BlockSchemaField {
  block_key: string;
  label: string;
  type: 'richtext';
  editable_by: 'admin' | 'manager' | 'all';
  max_length?: number;
  default_content?: string;
  help_text?: string;
}

export interface Template {
  id: number;
  tenant: number | null;
  template_key: string;
  name: string;
  category?: 'CONTRACT' | 'ANNEX';
  language: 'de' | 'en';
  scope: 'global' | 'tenant';
  active: boolean;
  contains_compensation: boolean;
  html_content?: string;
  html_file_url?: string;
  schema: {
    required_fields: SchemaField[];
    optional_fields: SchemaField[];
    all_keys?: string[];
  };
  blocks_schema?: BlockSchemaField[];
  schema_family?: 'MINIJOB_BASE_V2' | 'MINIJOB_COND_V1' | 'WERKSTUDENT_BASE_V1' | 'TEILZEIT_BASE_V1' | 'VOLLZEIT_BASE_V1' | 'KURZBEFRISTET_BASE_V1';
  created_at: string;
  updated_at: string;
  created_by?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface SchemaField {
  key: string;
  label: string;
  type: 'string' | 'number' | 'decimal' | 'date' | 'select' | 'text' | 'checkbox';
  default_value?: any;
  options?: SchemaFieldOption[];
  help_text?: string;
  profile_map?: string;
  editable_in?: 'wizard' | 'template';
  step_hint?: 'basic_info' | 'compensation' | 'clauses';
  constraints?: {
    min?: number;
    max?: number;
  };
}

export interface SchemaFieldOption {
  value: string | number;
  label: string;
}

export interface TemplateBinding {
  id: number;
  template: number;
  work_area?: number;
  work_area_name?: string;
  position_key?: string;
  position_label?: string;
  employment_type?: string;
  priority: number;
  created_at: string;
}

export interface TemplateListParams {
  search?: string;
  scope?: 'global' | 'tenant';
  active?: boolean;
  language?: 'de' | 'en';
  page?: number;
  page_size?: number;
}

export interface TemplateListResponse {
  count: number;
  results: Template[];
  next?: string | null;
  previous?: string | null;
}

export interface TemplateCreatePayload {
  template_key: string;
  name: string;
  language: 'de' | 'en';
  active: boolean;
  contains_compensation: boolean;
  html_content?: string;
  schema: {
    required_fields: SchemaField[];
    optional_fields: SchemaField[];
  };
}

export interface TemplateUpdatePayload {
  name?: string;
  language?: 'de' | 'en';
  active?: boolean;
  contains_compensation?: boolean;
  html_content?: string;
  schema?: {
    required_fields: SchemaField[];
    optional_fields: SchemaField[];
  };
}

export interface PreviewPayload {
  employee_id?: number;
  variables?: Record<string, any>;
}

export interface PreviewResponse {
  pdf_url?: string;
  pdf_bytes_b64?: string;
  pdf_base64?: string;
  resolved_variables: Record<string, any>;
  missing_required_fields: string[];
  render_status?: 'ok' | 'locked' | 'error';
  error_code?: string;
  message?: string;
}

export async function listTemplates(params?: TemplateListParams): Promise<TemplateListResponse> {
  const res = await axiosInstance.get('/api/admin/templates/', { params });

  // Robust gegen verschiedene Backend-Formate:
  // - Direktes Array: [...]
  // - DRF-Style: {results: [...], count: 10}
  // - Custom: {templates: [...], pagination: {...}}
  const data = res.data;

  let results: Template[];
  let count: number;
  let next: string | null = null;
  let previous: string | null = null;

  if (Array.isArray(data)) {
    // Direktes Array
    results = data;
    count = data.length;
  } else if (data.templates && Array.isArray(data.templates)) {
    // Custom Format: {templates: [...], pagination: {...}}
    results = data.templates;
    count = data.pagination?.total ?? results.length;
    next = data.pagination?.next ?? null;
    previous = data.pagination?.previous ?? null;
  } else {
    // DRF-Style: {results: [...], count: 10}
    results = data.results ?? data.items ?? [];
    count = data.count ?? results.length;
    next = data.next ?? null;
    previous = data.previous ?? null;
  }

  // Backend → Frontend mapping
  results = results.map((template: any) => ({
    ...template,
    template_key: template.template_key || template.key,
    html_content: template.html_content || template.html_source,
  }));

  return {
    count,
    results,
    next,
    previous,
  };
}

export async function getTemplate(id: number): Promise<Template> {
  const { data } = await axiosInstance.get<Template>(`/api/admin/templates/${id}/`);

  console.log('🔍 getTemplate - Received from backend:', {
    id: (data as any).id,
    hasSchema: !!data.schema,
    requiredFieldsCount: (data.schema as any)?.required_fields?.length || 0,
    optionalFieldsCount: (data.schema as any)?.optional_fields?.length || 0,
    htmlSourceLength: (data as any).html_source?.length || 0,
    htmlContentLength: (data as any).html_content?.length || 0,
  });

  // Backend → Frontend mapping
  return {
    ...data,
    template_key: (data as any).template_key || (data as any).key,
    html_content: (data as any).html_content || (data as any).html_source,
  };
}

export async function createTemplate(payload: TemplateCreatePayload): Promise<Template> {
  const backendPayload = {
    key: payload.template_key,
    name: payload.name,
    language: payload.language,
    active: payload.active,
    contains_compensation: payload.contains_compensation,
    html_source: payload.html_content,
    schema: payload.schema,
  };
  const { data } = await axiosInstance.post<Template>('/api/admin/templates/', backendPayload);

  // Backend → Frontend mapping
  return {
    ...data,
    template_key: (data as any).template_key || (data as any).key,
    html_content: (data as any).html_content || (data as any).html_source,
  };
}

export async function updateTemplate(id: number, payload: TemplateUpdatePayload): Promise<Template> {
  // Frontend → Backend mapping
  const backendPayload: any = {
    ...payload,
  };

  // Map html_content → html_source
  if (payload.html_content !== undefined) {
    backendPayload.html_source = payload.html_content;
    delete backendPayload.html_content;
  }

  console.log('🔍 updateTemplate - Sending to backend:', {
    id,
    hasSchema: !!backendPayload.schema,
    requiredFieldsCount: backendPayload.schema?.required_fields?.length || 0,
    optionalFieldsCount: backendPayload.schema?.optional_fields?.length || 0,
    htmlLength: backendPayload.html_source?.length || 0,
  });

  const { data } = await axiosInstance.patch<Template>(`/api/admin/templates/${id}/`, backendPayload);

  console.log('🔍 updateTemplate - Received from backend:', {
    hasSchema: !!data.schema,
    requiredFieldsCount: (data.schema as any)?.required_fields?.length || 0,
    optionalFieldsCount: (data.schema as any)?.optional_fields?.length || 0,
    htmlSourceLength: (data as any).html_source?.length || 0,
  });

  // Backend → Frontend mapping
  return {
    ...data,
    template_key: (data as any).template_key || (data as any).key,
    html_content: (data as any).html_content || (data as any).html_source,
  };
}

export async function duplicateTemplate(id: number): Promise<Template> {
  const { data } = await axiosInstance.post<Template>(`/api/admin/templates/${id}/duplicate/`);

  // Backend → Frontend mapping
  return {
    ...data,
    template_key: (data as any).template_key || (data as any).key,
    html_content: (data as any).html_content || (data as any).html_source,
  };
}

export async function toggleActive(id: number, active: boolean): Promise<Template> {
  const endpoint = active ? 'activate' : 'deactivate';
  const { data } = await axiosInstance.post<Template>(`/api/admin/templates/${id}/${endpoint}/`);

  // Backend → Frontend mapping
  return {
    ...data,
    template_key: (data as any).template_key || (data as any).key,
    html_content: (data as any).html_content || (data as any).html_source,
  };
}

export async function previewTemplate(id: number, payload: PreviewPayload): Promise<PreviewResponse> {
  return fetchWithRetry(async () => {
    const { data } = await axiosInstance.post(
      `/api/admin/templates/${id}/preview/`,
      payload
    );

    console.log('🔍 previewTemplate - Received from backend:', {
      hasPdfUrl: !!data.pdf_url,
      hasPdfBase64: !!data.pdf_base64,
      pdfUrlLength: data.pdf_url?.length || 0,
      pdfBase64Length: data.pdf_base64?.length || 0,
      resolvedVariablesCount: Object.keys(data.resolved_variables || {}).length,
      missingFieldsCount: data.missing_required_fields?.length || 0,
    });

    let pdf_url = data.pdf_url;

    // Support both pdf_url and pdf_base64 formats from backend
    if (!pdf_url && data.pdf_base64) {
      pdf_url = `data:application/pdf;base64,${data.pdf_base64}`;
    }

    // Convert base64 data URL to Blob URL if needed
    if (pdf_url && pdf_url.startsWith('data:application/pdf;base64,')) {
      const base64Data = pdf_url.split(',')[1];
      const binaryString = window.atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      pdf_url = URL.createObjectURL(blob);
    }

    return {
      pdf_url,
      resolved_variables: data.resolved_variables || {},
      missing_required_fields: data.missing_required_fields || [],
    };
  });
}

export async function listBindings(templateId: number): Promise<TemplateBinding[]> {
  const { data } = await axiosInstance.get<TemplateBinding[]>(
    `/api/admin/templates/${templateId}/bindings/`
  );
  return data;
}

export async function createBinding(
  templateId: number,
  payload: {
    work_area?: number;
    position_key?: string;
    employment_type?: string;
    priority: number;
  }
): Promise<TemplateBinding> {
  const { data } = await axiosInstance.post<TemplateBinding>(
    `/api/admin/templates/${templateId}/bindings/`,
    payload
  );
  return data;
}

export async function updateBinding(
  templateId: number,
  bindingId: number,
  payload: {
    work_area?: number;
    position_key?: string;
    employment_type?: string;
    priority?: number;
  }
): Promise<TemplateBinding> {
  const { data } = await axiosInstance.patch<TemplateBinding>(
    `/api/admin/templates/${templateId}/bindings/${bindingId}/`,
    payload
  );
  return data;
}

export async function deleteBinding(templateId: number, bindingId: number): Promise<void> {
  await axiosInstance.delete(`/api/admin/templates/${templateId}/bindings/${bindingId}/`);
}

export interface SeedPackPayload {
  pack: string;
}

export interface SeedPackResponse {
  created_count: number;
  skipped_count: number;
  template_keys: string[];
}

export async function seedTemplates(payload: SeedPackPayload): Promise<SeedPackResponse> {
  const { data } = await axiosInstance.post<SeedPackResponse>(
    '/api/admin/templates/seed/',
    payload
  );
  return data;
}

export interface LoadSchemaPayload {
  schema_family: string;
  employee_id?: number;  // ← Optional: für employee-spezifische default_values
}

export interface LoadSchemaResponse {
  required_fields: SchemaField[];
  optional_fields: SchemaField[];
  default_values?: Record<string, any>;  // ← Optional: employee-spezifische Defaults
}

export async function loadSchema(templateId: number, payload: LoadSchemaPayload): Promise<LoadSchemaResponse> {
  const { data } = await axiosInstance.post<LoadSchemaResponse>(
    `/api/admin/templates/${templateId}/schema/load/`,
    payload
  );
  return data;
}

export interface TemplateSummary {
  id: number;
  template_key: string;
  name: string;
  description?: string;
  category?: string;
}

export async function getAnnexTemplates(): Promise<TemplateSummary[]> {
  const response = await listTemplates({
    scope: 'global',
    active: true,
  });

  return response.results
    .filter(tpl => tpl.template_key?.startsWith('ANNEX_'))
    .map(tpl => ({
      id: tpl.id,
      template_key: tpl.template_key,
      name: tpl.name,
      description: undefined,
      category: 'ANNEX',
    }));
}
